import React from 'react';
import { Card, CardHeader, FormField, Input, Select, Textarea } from '../../components/ui';

/**
 * Renders a controlled form from its server-side template. Adding ICT-LFRM-004
 * is a change to config/governanceForms.js — no new component, route or table.
 *
 * Every section lays out on a two-column grid; a field's `span` decides whether
 * it takes half a row or the whole one, so tall sections fit the dialog rather
 * than scrolling.
 *
 * Field types: text | textarea | date | select | user
 */
const Control = ({ field, value, onChange, disabled, users = [], full = false }) => {
    const common = {
        id: `gv-${field.key}`,
        value: value ?? '',
        onChange: (e) => onChange(field.key, e.target.value),
        disabled,
        placeholder: field.placeholder,
    };

    if (field.type === 'textarea') {
        // A half-width box sits beside a select, so it stays shallow; a
        // full-width one is the section's long answer and gets more room.
        return full
            ? <Textarea rows={3} style={{ minHeight: 64 }} {...common} />
            : <Textarea rows={2} style={{ minHeight: 52 }} {...common} />;
    }
    if (field.type === 'date') return <Input type="date" {...common} />;

    if (field.type === 'user') {
        return (
            <Select {...common}>
                <option value="">Select a person…</option>
                {users.map((u) => {
                    const name = `${u.first_name} ${u.last_name}`;
                    return (
                        <option key={u.id} value={name}>
                            {name} — {u.role}
                        </option>
                    );
                })}
            </Select>
        );
    }

    if (field.type === 'select') {
        // A stored value that has since been removed from the list must still
        // show, or editing an old record would silently blank the field.
        const options = field.options || [];
        const orphan = value && !options.includes(value) ? [value] : [];
        return (
            <Select {...common}>
                <option value="">Select…</option>
                {[...options, ...orphan].map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </Select>
        );
    }

    return <Input type="text" {...common} />;
};

/**
 * Width of one field in the two-column grid. Half by default — including
 * textareas — so a section packs even when the template predates `span`; only
 * an explicit 'full' claims the whole row.
 */
const isFullWidth = (field) => field.span === 'full';

/** Just the fields of one section — used inside the tabbed record modal. */
export const SectionFields = ({ section, values = {}, onChange, disabled = false, users = [] }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 items-start">
        {section.fields.map((field) => (
            <FormField
                key={field.key}
                label={field.label}
                htmlFor={`gv-${field.key}`}
                required={field.required}
                className={isFullWidth(field) ? 'md:col-span-2' : ''}
            >
                <Control
                    field={field}
                    value={values[field.key]}
                    onChange={onChange}
                    disabled={disabled}
                    users={users}
                    full={isFullWidth(field)}
                />
            </FormField>
        ))}
    </div>
);

/** Read-only rendering of the same template, used on the record detail page. */
export const FormSummary = ({ form, values = {} }) => {
    if (!form) return null;

    return (
        <div className="space-y-4">
            {form.sections.map((section) => {
                const answered = section.fields.filter((f) => values[f.key]);
                if (section.optional && answered.length === 0) return null;

                return (
                    <Card key={section.key}>
                        <CardHeader title={section.title} />
                        <table className="table">
                            <tbody>
                                {section.fields.map((field) => (
                                    <tr key={field.key}>
                                        <th
                                            scope="row"
                                            className="w-[38%] font-medium text-ink-700 bg-[var(--bg-subtle)]"
                                        >
                                            {field.label}
                                        </th>
                                        <td className="whitespace-pre-line align-top">
                                            {values[field.key] || <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                );
            })}
        </div>
    );
};

export default SectionFields;
